import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Card,
  CardContent,
  CardHeader,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  CircularProgress,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  IconButton,
  Tooltip,
  Badge
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import {
  ExpandMore,
  Search,
  FilterList,
  BugReport,
  Add,
  Update,
  Security,
  Remove,
  Warning,
  Info,
  Timeline,
  Code,
  Description
} from "@material-ui/icons";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
  // ✅ CORREGIDO: Root dinámico según tema
  root: {
    padding: theme.spacing(3),
    backgroundColor: theme.mode === 'dark' ? '#1f2937' : "#f5f5f5",
    minHeight: "100vh"
  },
  // ✅ CORREGIDO: Header dinámico según tema (mantiene gradiente pero adaptado)
  header: {
    marginBottom: theme.spacing(3),
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    color: "white",
    padding: theme.spacing(3),
    borderRadius: theme.spacing(1),
    boxShadow: theme.mode === 'dark' ? '0 4px 20px rgba(102, 126, 234, 0.3)' : '0 4px 20px rgba(0,0,0,0.1)',
  },
  // ✅ CORREGIDO: SearchContainer dinámico según tema
  searchContainer: {
    marginBottom: theme.spacing(3),
    display: "flex",
    gap: theme.spacing(2),
    alignItems: "center",
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.spacing(1),
    border: `1px solid ${theme.mode === 'dark' ? '#4B5563' : '#e0e0e0'}`,
    boxShadow: theme.mode === 'dark' ? '0 2px 8px rgba(0,0,0,0.3)' : '0 2px 8px rgba(0,0,0,0.08)',
  },
  // ✅ CORREGIDO: VersionCard dinámico según tema
  versionCard: {
    marginBottom: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
    transition: "all 0.3s ease",
    border: `1px solid ${theme.mode === 'dark' ? '#4B5563' : '#e0e0e0'}`,
    boxShadow: theme.mode === 'dark' ? '0 2px 8px rgba(0,0,0,0.3)' : '0 2px 8px rgba(0,0,0,0.08)',
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: theme.mode === 'dark' ? theme.shadows[12] : theme.shadows[8]
    }
  },
  versionHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(1)
  },
  versionBadge: {
    fontWeight: "bold",
    fontSize: "1.1rem"
  },
  categoryIcon: {
    marginRight: theme.spacing(1)
  },
  changeItem: {
    paddingLeft: theme.spacing(2),
    borderLeft: `3px solid ${theme.palette.primary.main}`,
    marginBottom: theme.spacing(1)
  },
  statsContainer: {
    display: "flex",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1)
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "200px"
  },
  // ✅ CORREGIDO: TextField dinámico según tema
  textField: {
    '& .MuiOutlinedInput-root': {
      borderRadius: 8,
      backgroundColor: theme.mode === 'dark' ? '#374151' : '#f5f5f5',
      color: theme.palette.text.primary,
      '&:hover': {
        backgroundColor: theme.mode === 'dark' ? '#4B5563' : '#eeeeee',
      },
      '&.Mui-focused': {
        backgroundColor: theme.mode === 'dark' ? '#374151' : 'white',
        boxShadow: `0 0 0 2px ${theme.mode === 'dark' ? 'rgba(59, 130, 246, 0.3)' : 'rgba(25, 118, 210, 0.2)'}`,
      },
      "& fieldset": {
        borderColor: theme.mode === 'dark' ? '#4B5563' : '#e0e0e0',
      },
      "&:hover fieldset": {
        borderColor: theme.palette.primary.main,
      },
      "&.Mui-focused fieldset": {
        borderColor: theme.palette.primary.main,
      },
    },
    "& .MuiInputLabel-root": {
      color: theme.palette.text.secondary || theme.palette.text.primary,
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: theme.palette.primary.main,
    },
  },
  // ✅ CORREGIDO: FormControl dinámico según tema
  formControl: {
    '& .MuiOutlinedInput-root': {
      backgroundColor: theme.mode === 'dark' ? '#374151' : '#f5f5f5',
      color: theme.palette.text.primary,
      '&:hover': {
        backgroundColor: theme.mode === 'dark' ? '#4B5563' : '#eeeeee',
      },
      "& fieldset": {
        borderColor: theme.mode === 'dark' ? '#4B5563' : '#e0e0e0',
      },
      "&:hover fieldset": {
        borderColor: theme.palette.primary.main,
      },
      "&.Mui-focused fieldset": {
        borderColor: theme.palette.primary.main,
      },
    },
    "& .MuiInputLabel-root": {
      color: theme.palette.text.secondary || theme.palette.text.primary,
      "&.Mui-focused": {
        color: theme.palette.primary.main,
      },
    },
    "& .MuiSelect-root": {
      color: theme.palette.text.primary,
    },
    "& .MuiMenuItem-root": {
      backgroundColor: theme.palette.background.paper,
      color: theme.palette.text.primary,
      "&:hover": {
        backgroundColor: theme.mode === 'dark' ? '#374151' : '#f5f5f5',
      },
    },
  },
}));

const categoryIcons = {
  added: <Add color="primary" />,
  changed: <Update color="action" />,
  fixed: <BugReport color="secondary" />,
  deprecated: <Warning color="warning" />,
  removed: <Remove color="error" />,
  security: <Security color="error" />
};

const categoryLabels = {
  added: "Nuevas Funcionalidades",
  changed: "Cambios",
  fixed: "Correcciones",
  deprecated: "Obsoletos",
  removed: "Eliminados",
  security: "Seguridad"
};

const categoryColors = {
  added: "primary",
  changed: "default",
  fixed: "secondary",
  deprecated: "warning",
  removed: "error",
  security: "error"
};

const ReleasesPage = () => {
  const classes = useStyles();
  const theme = useTheme();
  const [changelogData, setChangelogData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterCategory, setFilterCategory] = useState("all");
  const [expandedVersion, setExpandedVersion] = useState(null);

  useEffect(() => {
    loadChangelog();
  }, []);

  const loadChangelog = async () => {
    try {
      setLoading(true);
      const { data } = await api.get("/system/changelog");
      setChangelogData(data);
    } catch (err) {
      setError(err.response?.data?.error || "Error al cargar el changelog");
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleVersionToggle = (version) => {
    setExpandedVersion(expandedVersion === version ? null : version);
  };

  const filteredVersions = changelogData?.versions?.filter(version => {
    const matchesSearch = searchTerm === "" || 
      version.version.includes(searchTerm) ||
      version.content.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesCategory = filterCategory === "all" || 
      version.categories[filterCategory]?.length > 0;
    
    return matchesSearch && matchesCategory;
  }) || [];

  const getTotalChanges = () => {
    return changelogData?.versions?.reduce((total, version) => total + version.totalChanges, 0) || 0;
  };

  const getLatestVersion = () => {
    return changelogData?.versions?.[0]?.version || "N/A";
  };

  if (loading) {
    return (
      <Box className={classes.loadingContainer}>
        <CircularProgress size={60} />
        <Typography variant="h6" style={{ marginLeft: 16, color: theme.palette.text.primary }}>
          Cargando historial de versiones...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box className={classes.root}>
        <Alert severity="error">
          <Typography variant="h6">Error al cargar versiones</Typography>
          <Typography>{error}</Typography>
        </Alert>
      </Box>
    );
  }

  return (
    <Box className={classes.root}>
      {/* Header */}
      <Paper className={classes.header}>
        <Box display="flex" alignItems="center" marginBottom={2}>
          <Timeline style={{ marginRight: 16, fontSize: 32 }} />
          <Box>
            <Typography variant="h4" component="h1">
              Historial de Versiones
            </Typography>
            <Typography variant="subtitle1">
              Seguimiento completo de cambios y mejoras del sistema
            </Typography>
          </Box>
        </Box>
        
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Box display="flex" alignItems="center">
              <Code style={{ marginRight: 8, color: 'white' }} />
              <Typography variant="body1" style={{ color: 'white' }}>
                <strong>Última versión:</strong> v{getLatestVersion()}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box display="flex" alignItems="center">
              <Description style={{ marginRight: 8, color: 'white' }} />
              <Typography variant="body1" style={{ color: 'white' }}>
                <strong>Total de cambios:</strong> {getTotalChanges()}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box display="flex" alignItems="center">
              <Timeline style={{ marginRight: 8, color: 'white' }} />
              <Typography variant="body1" style={{ color: 'white' }}>
                <strong>Versiones:</strong> {changelogData?.versions?.length || 0}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Filtros y búsqueda */}
      <Paper className={classes.searchContainer} elevation={2}>
        <TextField
          placeholder="Buscar versiones o cambios..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search style={{ color: theme.palette.text.secondary || theme.palette.text.primary }} />
              </InputAdornment>
            )
          }}
          className={classes.textField}
          style={{ flex: 1 }}
        />
        
        <FormControl className={classes.formControl} style={{ minWidth: 200 }}>
          <InputLabel>Categoría</InputLabel>
          <Select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            startAdornment={<FilterList style={{ color: theme.palette.text.secondary || theme.palette.text.primary }} />}
          >
            <MenuItem value="all">Todas las categorías</MenuItem>
            <MenuItem value="added">Nuevas funcionalidades</MenuItem>
            <MenuItem value="changed">Cambios</MenuItem>
            <MenuItem value="fixed">Correcciones</MenuItem>
            <MenuItem value="security">Seguridad</MenuItem>
            <MenuItem value="deprecated">Obsoletos</MenuItem>
            <MenuItem value="removed">Eliminados</MenuItem>
          </Select>
        </FormControl>
      </Paper>

      {/* Lista de versiones */}
      <Box>
        {filteredVersions.length === 0 ? (
          <Paper elevation={2} style={{ 
            padding: 24, 
            textAlign: "center",
            backgroundColor: theme.palette.background.paper,
            border: `1px solid ${theme.mode === 'dark' ? '#4B5563' : '#e0e0e0'}`,
          }}>
            <Typography variant="h6" style={{ color: theme.palette.text.primary }}>
              No se encontraron versiones que coincidan con los filtros
            </Typography>
          </Paper>
        ) : (
          filteredVersions.map((version, index) => (
            <Card key={`${version.version}-${version.date}-${index}`} className={classes.versionCard} elevation={2}>
              <CardHeader
                title={
                  <Box className={classes.versionHeader}>
                    <Box display="flex" alignItems="center">
                      <Chip
                        label={`v${version.version}`}
                        color={index === 0 ? "primary" : "default"}
                        className={classes.versionBadge}
                        size="medium"
                      />
                      <Typography variant="h6" style={{ marginLeft: 16, color: theme.palette.text.primary }}>
                        {version.date}
                      </Typography>
                    </Box>
                    <Box display="flex" alignItems="center">
                      <Badge badgeContent={version.totalChanges} color="secondary">
                        <Typography variant="body2" style={{ color: theme.palette.text.secondary || theme.palette.text.primary }}>
                          {version.totalChanges} cambios
                        </Typography>
                      </Badge>
                      <Tooltip title={version.totalChanges > 0 ? "Ver detalles" : "Ver detalles (sin cambios)"}>
                        <IconButton onClick={() => handleVersionToggle(version.version)} style={{ color: theme.palette.text.primary }}>
                          <ExpandMore />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                }
              />
              
              {expandedVersion === version.version && (
                <CardContent style={{ backgroundColor: theme.palette.background.paper }}>
                  <Divider style={{ marginBottom: 16, backgroundColor: theme.mode === 'dark' ? '#4B5563' : '#e0e0e0' }} />
                  
                  {version.totalChanges > 0 ? (
                    <>
                      {/* Estadísticas por categoría */}
                      <Box className={classes.statsContainer}>
                        {Object.entries(version.categories).map(([category, items]) => {
                          if (items.length === 0) return null;
                          return (
                            <Chip
                              key={category}
                              icon={categoryIcons[category]}
                              label={`${categoryLabels[category]}: ${items.length}`}
                              color={categoryColors[category]}
                              variant="outlined"
                              size="small"
                            />
                          );
                        })}
                      </Box>
                      
                      <Divider style={{ margin: "16px 0", backgroundColor: theme.mode === 'dark' ? '#4B5563' : '#e0e0e0' }} />
                      
                      {/* Detalles por categoría */}
                      {Object.entries(version.categories).map(([category, items]) => {
                        if (items.length === 0) return null;
                        return (
                          <Box key={category} style={{ marginBottom: 16 }}>
                            <Typography variant="h6" gutterBottom style={{ display: "flex", alignItems: "center", color: theme.palette.text.primary }}>
                              {categoryIcons[category]}
                              <span style={{ marginLeft: 8 }}>{categoryLabels[category]}</span>
                            </Typography>
                            <List dense>
                              {items.map((item, itemIndex) => (
                                <ListItem key={itemIndex} className={classes.changeItem} style={{ backgroundColor: theme.mode === 'dark' ? 'transparent' : 'transparent' }}>
                                  <ListItemIcon>
                                    <Info style={{ color: theme.palette.text.secondary || theme.palette.text.primary }} fontSize="small" />
                                  </ListItemIcon>
                                  <ListItemText 
                                    primary={item}
                                    primaryTypographyProps={{ variant: "body2", style: { color: theme.palette.text.primary } }}
                                  />
                                </ListItem>
                              ))}
                            </List>
                          </Box>
                        );
                      })}
                    </>
                  ) : (
                    /* Mensaje cuando no hay cambios */
                    <Box style={{ textAlign: "center", padding: "24px" }}>
                      <Typography variant="h6" style={{ color: theme.palette.text.primary }} gutterBottom>
                        <span role="img" aria-label="Documento">📋</span> Sin cambios registrados
                      </Typography>
                      <Typography variant="body2" style={{ color: theme.palette.text.secondary || theme.palette.text.primary }}>
                        Esta versión no tiene cambios documentados en el changelog.
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              )}
            </Card>
          ))
        )}
      </Box>
    </Box>
  );
};

export default ReleasesPage;
